import { Link } from "react-router-dom";
import { Flame, TrendingDown, MessageCircle, MapPin } from "lucide-react";
import { CONTACT } from "../data/site";
import { Reveal, Stagger, Item } from "./shared";
import { useI18n } from "../i18n/LanguageContext";
import { useDeals } from "../hooks/useDeals";

const DealCard = ({ d, enquire }) => (
  <Item>
    <div className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-card" data-testid={`distress-card-${d.id}`}>
      <div className="relative aspect-[16/11] overflow-hidden">
        <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
        <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-red-500 px-3 py-1.5 text-sm font-semibold text-white shadow-lg"><TrendingDown size={15} /> −{d.drop}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-1.5 text-muted"><MapPin size={14} className="text-gold" /><span className="text-sm">{d.location} · {d.developer}</span></div>
        <h3 className="mt-2 font-display text-2xl font-semibold tracking-tight text-navy">{d.name}</h3>
        <p className="mt-1 text-sm text-muted">{d.beds}</p>
        <div className="mt-5 flex items-end gap-3"><span className="text-sm text-muted line-through decoration-red-400">{d.oldPrice} {d.currency}</span></div>
        <span className="font-display text-2xl font-semibold text-navy">{d.newPrice} <span className="text-sm font-medium text-muted">{d.currency}</span></span>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-sand px-3 py-2.5"><Flame size={14} className="shrink-0 text-red-500" /><span className="text-xs font-medium text-navy/80">{d.reason}</span></div>
        <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid={`distress-enquire-${d.id}`} className="btn-primary mt-6 w-full"><MessageCircle size={16} /> {enquire}</a>
      </div>
    </div>
  </Item>
);

export const DistressDeals = () => {
  const { t, c } = useI18n();
  const items = useDeals(c.DISTRESS_DEALS);
  return (
    <section id="distress" className="relative overflow-hidden bg-navy py-20 sm:py-28" data-testid="distress-section">
      <div className="pointer-events-none absolute -right-20 top-10 h-80 w-80 rounded-full bg-gold/15 blur-[130px]" />
      <div className="container-x relative">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-red-500/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-red-300"><Flame size={13} /> {t.distress.eyebrow}</span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">{t.distress.title} <span className="text-gold-300">{t.distress.accent}</span></h2>
            </Reveal>
            <Reveal delay={0.16}><p className="mt-5 text-base leading-relaxed text-white/70">{t.distress.sub}</p></Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link to="/distress" data-testid="distress-view-all" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-navy">
              {t.distress.viewAll} <TrendingDown size={16} />
            </Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.slice(0, 3).map((d) => <DealCard key={d.id} d={d} enquire={t.distress.enquire} />)}
        </Stagger>
      </div>
    </section>
  );
};
